import React, { useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  Modal,
  Table,
  Row,
  Col,
  DatePicker,
  Select,
  Input, Divider, Tabs
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import { usePostRequest } from "../../hooks/usePostRequest";
import { UsePostRequest } from "../../utils/api/ApiRequests";
import dayjs from "dayjs";
import { GetAllEmployees } from "../../utils/api/DropdownFiltersApi";
import TextArea from "antd/es/input/TextArea";
import {
  formatAmount,
  formatDate,
  removeUnderscoreToSentence,
  getIsAdminLogin,
  getEmployeeId,
  capitalizeEachWord,
} from "../../utils/Common";

const AddSubmitButton = ({ form, isLoading }) => {
  const [submittable, setSubmittable] = useState(false);
  const values = Form.useWatch([], form);
  React.useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then(
        () => {
          setSubmittable(true);
        },
        () => {
          setSubmittable(false);
        }
      );
  }, [values]);

  return (
    <Button
      type="primary"
      htmlType="submit"
      disabled={!submittable}
      loading={isLoading}
    >
      Submit
    </Button>
  );
};

export default function Loan() {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const employeeOptions = GetAllEmployees();
  const dateFormat = "YYYY-MM-DD";
  const isAdminLogin = getIsAdminLogin();
  const employeeId = getEmployeeId();

  const { callPost, isLoading } = usePostRequest(() => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowViewModal(false);
    setShowPaymentModal(false);
    resetValues(); 
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }));
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [clickedRowData, setClickedRowData] = useState({});
  // const [initialValues, setInitialValues] = useState({});
  const [tableData, setTableData] = useState([]);
  const [modal, contextHolder] = Modal.useModal();
  const [searchTerm, setSearchTerm] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [filterConfig, setFilterConfig] = useState({
    is_updated: false,
    employee_id: isAdminLogin ? null : employeeId,
    status: "pending",
  });
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: "",
  });

  const [isFetching, setIsFetching] = useState(false);

  const tableColumns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (data, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{data}</div>,
    },
    {
      title: "Loan Type",
      dataIndex: "loan_type",
      key: "loan_type",
      render: (loan_type, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{removeUnderscoreToSentence(loan_type)}</div>,
    },
    {
      title: "Issue Date",
      dataIndex: "issue_date",
      key: "issue_date",
      render: (issue_date, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{formatDate(issue_date)}</div>,
    },
    {
      title: "Loan Amount",
      dataIndex: "loan_amount",
      key: "loan_amount",
      align: "right",
      render: (loan_amount, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{formatAmount(loan_amount)}</div>,
    },
    {
      title: "Paid Amount",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right",
      render: (paid_amount, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{formatAmount(paid_amount)}</div>,
    },
    {
      title: "Remaining Balance",
      dataIndex: "remaining_balance",
      key: "remaining_balance",
      align: "right",
      render: (remaining_balance, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{formatAmount(remaining_balance)}</div>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "",
      render: (data, record) => <div className="pointer-cursor" onClick={()=>handleRowClick(record)}>{capitalizeEachWord(data)}</div>,
    },
    {
      title: "",
      dataIndex: "payment",
      key: "payment",
      render: (data,record) => <div>
        {(record.status==="approved"&&parseFloat(record.remaining_balance)>0)&&<Button onClick={() => {setClickedRowData(record); setShowPaymentModal(true)}}>ADD PAYMENT</Button>}
      </div>
    }
  ];

  const loanTypes = [
    { value: "sss_loan", label: "SSS Loan" },
    { value: "pagibig_loan", label: "Pag-IBIG Loan" },
    { value: "ppe_loan", label: "PPE's Loan" },
    { value: "cash_advance", label: "Cash Advance" },
    { value: "cooperative_loan", label: "Cooperative Loan" },
    { value: "others", label: "Others" },
  ];

  const termTypes = [
    { value: "per_payroll", label: "Per Payroll" },
    { value: "end_month", label: "End Month" },
  ];

  const onFinish = (formInputs) => {
    formInputs.issue_date = formInputs.issue_date.format(dateFormat);
    formInputs.status = "pending";
    callPost("employee_loans/add", formInputs, false);
  };

  const onEdit = (formInputs) => {
    formInputs.issue_date = formInputs.issue_date.format(dateFormat);
    formInputs.loan_id = clickedRowData.id;
    formInputs.status = clickedRowData.status;
    callPost(`employee_loans/update`, formInputs, false);
  };

  const onSubmitPayment = () => {
    paymentForm.employee_loan_id = clickedRowData.id;
    callPost("employee_loans/update_loan_payment", paymentForm, false);
  }

  const confirmDelete = (loan) => {
    modal.confirm({
      title: `Are you sure you want to delete this loan"?`,
      icon: <ExclamationCircleOutlined />,
      content: "You cannot undo this action.",
      okText: "Delete",
      cancelText: "Cancel",
      onOk: () => handleDelete(loan.id),
      okType: "danger",
    });
  };

  const confirmPayment = (loan, status) => {
    modal.confirm({
      title: `Are you sure you want to add this payment?`,
      icon: <CheckCircleOutlined />,
        content: "You cannot undo this action.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => onSubmitPayment(),
      //   okType: "danger",
    });
  };

  const confirmApprove = (loan, status) => {
    modal.confirm({
      title: `Are you sure you want to ${status} ${loan.employee_name}'s loan?`,
      icon: <CheckCircleOutlined />,
      //   content: "You cannot undo this action.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: () => handleApprove(loan.id, status),
      //   okType: "danger",
    });
  };

  function handleApprove(passedId, status) {
    const newStatus = `${status}d`;
    callPost(
      "employee_loans/update_status",
      { employee_loan_id: passedId, status: newStatus },
      false
    );
    setShowViewModal(false);
    setFilterConfig((prev) => ({ ...prev, is_updated: !prev.is_updated }));
  }

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setFilterConfig((prev) => ({ ...prev, term: searchTerm }));
    }
  };

  function handleDelete(passedId) {
    callPost("employee_loans/delete", { loan_id: passedId }, false);
  }

  const handleRowClick = (rowData) => {
    if (isAdminLogin) {
      setClickedRowData(rowData);
      const formattedValues = { ...rowData };
      formattedValues.issue_date = rowData.issue_date
        ? dayjs(rowData.issue_date, dateFormat)
        : "";
      form.setFieldsValue(formattedValues);
      setShowViewModal(true);
    } else {
      navigate("view", { state: rowData });
    }
  };

  function resetValues () {
    form.resetFields();
    setPaymentForm({paid_amount: ""});
  }

  function exportToExcel () {
    const excelData = [tableColumns.map((col) => col.title)]; // insert header titles first
    // populate excel data
    tableData.forEach((data) => {
      let tempArray = tableColumns.map((col) => data[col.key])
      excelData.push(tempArray)
    })
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBlob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' });
    const excelUrl = URL.createObjectURL(excelBlob);
    const downloadAnchor = document.createElement('a');
    downloadAnchor.href = excelUrl;
    downloadAnchor.download = 'Employee Loans.xlsx';
    downloadAnchor.click();
    setTimeout(() => {
      URL.revokeObjectURL(excelUrl);
    }, 100);
}

  React.useEffect(() => {
    async function fetchTableData() {
      setIsFetching(true);
      setTableData([]);
      const response = await UsePostRequest("employee_loans/get", filterConfig);
      if (response.data) {
        setTableData(response.data);
      }
      setIsFetching(false);
    }

    fetchTableData();
  }, [filterConfig]);

  return (
    <Navbar
      activeNav="loan"
      pageTitle="Loans"
      pageButtonTitle="Add Loan"
      setSearchQuery={(e) => setSearchTerm(e)}
      onSearch={handleSearch}
      searchPlaceholder="employee"
      onClickButton={() => setShowAddModal(true)}
      isManager
      searchable={isAdminLogin}
      noButton={!isAdminLogin}
      pageDownloadButtonTitle="Download Excel" 
      isDownloadBtn
      onClickDownloadButton = {exportToExcel}
    >
      <Tabs 
        defaultActiveKey="pending"
        className="mt-4" 
        items={[
          {key: "pending", label: "Pending"},
          {key: "approved", label: "Approved"},
          {key: "disapproved", label: "Disapproved"},
          {key: "", label: "All"},
        ]}
        type="card"
        onChange={(e) => setFilterConfig((prev) => ({...prev, status:e}))}
      />
      <Table
        className=""
        rowKey="id"
        // onRow={(record) => ({
        //   ...rowProps(record),
        //   style: { cursor: "pointer" },
        // })}
        columns={tableColumns}
        dataSource={tableData}
        loading={isFetching || isLoading}
      />

      <Modal
      maskClosable={false}
        title={`${showAddModal ? "Add" : "Edit"} Loan`}
        centered
        open={showAddModal || showEditModal}
        footer={null}
        onCancel={() => {
          setShowAddModal(false);
          setShowEditModal(false);
          form.resetFields();
        }}
        width={800}
      >
        <Divider/>
        <Form
          className="mt-3 mx-3"
          form={form}
          name="validateOnly"
          layout="vertical"
          onFinish={showAddModal ? onFinish : onEdit}
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select
                  options={employeeOptions.slice(1)}
                  disabled={showEditModal}
                  showSearch
                  filterOption={(input, option) =>
                    option.label?.toLowerCase().includes(input?.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="issue_date"
                label="Issue Date"
                rules={[
                  {
                    required: true
                  },
                ]}
              >
                <DatePicker placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_type"
                label="Loan Type"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={loanTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_amount"
                label="Loan Amount"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input type="number" onWheel={(e)=>e.target.blur()}/>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="term"
                label="Term"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={termTypes} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="installment"
                label="Installment"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input type="number" onWheel={(e)=>e.target.blur()} />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="reason"
                label="Reason"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <TextArea />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Row>
                <Button
                  className="me-2"
                  danger
                  onClick={() => {
                    setShowAddModal(false);
                    setShowEditModal(false);
                    form.resetFields();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <AddSubmitButton form={form} isLoading={isLoading} />
              </Row>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Review Loan"
        centered
        open={showViewModal}
        footer={null}
        onCancel={() => {
          setShowViewModal(false);
          form.resetFields();
        }}
        width={800}
      >
        <Form
          className="mt-3 mx-3"
          form={form}
          name="editForm"
          layout="vertical"
        >
          <Row>
            <Col xs={24} lg={12}>
              <Form.Item
                name="employee_id"
                label="Employee"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={employeeOptions.slice(1)} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="issue_date"
                label="Issue Date"
              >
                <DatePicker disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_type"
                label="Loan Type"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={loanTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="loan_amount"
                label="Loan Amount"
                rules={[
                  {
                    required: true, 
                  },
                ]}
              >
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="term"
                label="Term"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <Select options={termTypes} disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="installment"
                label="Installment"
              >
                <Input type="number" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} lg={24}>
              <Form.Item
                name="reason"
                label="Reason"
                rules={[
                  {
                    required: true, whitespace: true,
                  },
                ]}
              >
                <TextArea disabled />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Row className="d-flex justify-content-end">
              <Button
                danger
                onClick={() => {setShowViewModal(false); form.resetFields();}}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="text"
                onClick={() => {
                  setShowViewModal(false);
                  setShowEditModal(true);
                }}
                icon={<EditOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Edit
              </Button>
              {
                isAdminLogin &&
                <>
                <Button
                  type="text"
                  onClick={() => {
                    confirmApprove(clickedRowData, "approve");
                  }}
                  icon={<CheckCircleOutlined />}
                  disabled={clickedRowData.status === "approved"}
                >
                  Approve
                </Button>
                <Button
                  type="text"
                  onClick={() => {
                    confirmApprove(clickedRowData, "disapprove");
                  }}
                  icon={<CloseCircleOutlined />}
                  disabled={clickedRowData.status === "disapproved"}
                  style={{ color: "red" }}
                >
                  Disapprove
                </Button>
                </>
              }
              <Button
                type="text"
                onClick={() => {
                  navigate("view", { state: clickedRowData });
                }}
                icon={<EyeOutlined />}
              >
                View
              </Button>
              <Button
                type="text"
                danger
                onClick={() => {
                  confirmDelete(clickedRowData);
                }}
                icon={<DeleteOutlined />}
                disabled={clickedRowData.status === "approved"}
              >
                Delete
              </Button>
            </Row>
          </Form.Item>
        </Form>
      </Modal>

      <Modal open={showPaymentModal} title="Add Loan Payment"
        centered
        footer={null}
        onCancel={() => {
          setShowPaymentModal(false); resetValues();
        }}
          >
          <div>
            <Divider/>
            <Row>
              <Col xs={6} lg={6}>Employee</Col>
              <Col xs={2} className="text-center">:</Col>
              <Col className="w-100" xs={16} lg={12}>{clickedRowData.employee_name}</Col>
            </Row>
            <Row className="mt-1">
              <Col xs={6} lg={6}>Balance</Col>
              <Col xs={2} className="text-center">:</Col>
              <Col lg={6} xs={16} className="bold">{formatAmount(clickedRowData.remaining_balance)}</Col>
            </Row>
            <Row className="mt-1">
              <Col xs={6} lg={6}>Paid Amount</Col>
              <Col xs={2} className="text-center">:</Col>
              <Col xs={16} lg={12}><Input type="number" className="w-100" value={paymentForm.paid_amount} onChange={(e) => setPaymentForm((prev)=>({...prev, paid_amount: e.target.value}))}/></Col>
            </Row>
            <Row className="mt-4 d-flex justify-content-end">
              <Button
                danger
                onClick={() => {setShowPaymentModal(false); resetValues()}}
                disabled={isLoading}
                className="me-2"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={confirmPayment}
                icon={<EditOutlined />}
                disabled={paymentForm.paid_amount===''||paymentForm.paid_amount<=0||paymentForm.paid_amount>parseFloat(clickedRowData.remaining_balance)}
              >
                Submit
              </Button>
            </Row>
          </div>
        </Modal>

      {contextHolder}
    </Navbar>
  );
}
